import * as React from "react";
import { CalendarClock, X, ChevronUp, ChevronDown } from "lucide-react";
import { format, parse, isValid } from "date-fns";
import { hu } from "date-fns/locale";

import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// reuse the same styled calendar so the look is identical to DateInput
import { DateInput } from "@/components/ui/date-input";
import { DayPicker } from "react-day-picker";
import { ChevronLeft, ChevronRight } from "lucide-react";

const HU_MONTHS = [
  "Január", "Február", "Március", "Április", "Május", "Június",
  "Július", "Augusztus", "Szeptember", "Október", "November", "December",
];

function CalendarHeader({ displayMonth, fromYear, toYear, onChange }) {
  const month = displayMonth.getMonth();
  const year = displayMonth.getFullYear();
  const goto = (m, y) => {
    const next = new Date(displayMonth);
    next.setMonth(m); next.setFullYear(y); next.setDate(1);
    onChange(next);
  };
  const years = React.useMemo(() => {
    const arr = [];
    for (let y = toYear; y >= fromYear; y -= 1) arr.push(y);
    return arr;
  }, [fromYear, toYear]);
  return (
    <div className="mb-3 flex items-center justify-between gap-2 px-1">
      <button type="button"
        onClick={() => goto(month === 0 ? 11 : month - 1, month === 0 ? year - 1 : year)}
        className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground">
        <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
      <div className="flex flex-1 items-center justify-center gap-1.5">
        <div className="relative">
          <select value={month} onChange={(e) => goto(parseInt(e.target.value, 10), year)}
            className="appearance-none rounded-md bg-transparent px-2 py-1 pr-5 text-[13px] font-medium tracking-tight outline-none transition-colors hover:bg-foreground/[0.06] focus:bg-foreground/[0.06]">
            {HU_MONTHS.map((label, idx) => <option key={label} value={idx}>{label}</option>)}
          </select>
          <ChevronRight className="pointer-events-none absolute right-1 top-1/2 h-3 w-3 -translate-y-1/2 rotate-90 text-muted-foreground/70" />
        </div>
        <div className="relative">
          <select value={year} onChange={(e) => goto(month, parseInt(e.target.value, 10))}
            className="appearance-none rounded-md bg-transparent px-2 py-1 pr-5 text-[13px] font-medium tabular-nums tracking-tight outline-none transition-colors hover:bg-foreground/[0.06] focus:bg-foreground/[0.06]">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <ChevronRight className="pointer-events-none absolute right-1 top-1/2 h-3 w-3 -translate-y-1/2 rotate-90 text-muted-foreground/70" />
        </div>
      </div>
      <button type="button"
        onClick={() => goto(month === 11 ? 0 : month + 1, month === 11 ? year + 1 : year)}
        className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground">
        <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
    </div>
  );
}

function StyledCalendar({ selected, onSelect, locale, fromYear, toYear }) {
  const [displayMonth, setDisplayMonth] = React.useState(selected || new Date());
  React.useEffect(() => { if (selected) setDisplayMonth(selected); }, [selected]);
  return (
    <div className="p-3">
      <CalendarHeader displayMonth={displayMonth} fromYear={fromYear} toYear={toYear} onChange={setDisplayMonth} />
      <DayPicker
        mode="single"
        month={displayMonth}
        onMonthChange={setDisplayMonth}
        selected={selected}
        onSelect={onSelect}
        locale={locale}
        weekStartsOn={1}
        showOutsideDays
        classNames={{
          months: "", month: "", caption: "hidden", nav: "hidden",
          table: "w-full border-collapse", head_row: "flex",
          head_cell: "w-9 text-center text-[10.5px] font-medium uppercase tracking-[0.12em] text-muted-foreground/70",
          row: "mt-1 flex w-full",
          cell: "relative h-9 w-9 p-0 text-center text-[13px] focus-within:relative focus-within:z-20",
          day: "inline-flex h-9 w-9 items-center justify-center rounded-full font-normal tabular-nums transition-colors hover:bg-foreground/[0.06] focus:outline-none focus-visible:ring-1 focus-visible:ring-ring aria-selected:font-semibold",
          day_selected: "!bg-foreground !text-background hover:!bg-foreground hover:!text-background focus:!bg-foreground focus:!text-background",
          day_today: "ring-1 ring-foreground/15 ring-offset-0",
          day_outside: "text-muted-foreground/35",
          day_disabled: "text-muted-foreground/30 opacity-60",
          day_hidden: "invisible",
        }}
      />
    </div>
  );
}

function TimeStepper({ value, onChange, min = 0, max = 59, step = 1, testId }) {
  const inc = () => onChange((value + step) > max ? min : value + step);
  const dec = () => onChange((value - step) < min ? max : value - step);
  return (
    <div className="flex flex-col items-center">
      <button type="button" onClick={inc}
        className="grid h-6 w-10 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground">
        <ChevronUp className="h-3.5 w-3.5" />
      </button>
      <div data-testid={testId} className="my-1 grid h-12 w-14 place-items-center rounded-lg bg-foreground/[0.04] text-2xl font-semibold tabular-nums">
        {String(value).padStart(2, "0")}
      </div>
      <button type="button" onClick={dec}
        className="grid h-6 w-10 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground">
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

const TIME_PRESETS = ["08:00", "12:00", "14:00", "16:00", "18:00", "20:00"];

/**
 * Date+time picker matching DateInput's look.
 *
 * value:    ISO "YYYY-MM-DDTHH:mm" or "" — NOT pre-filled by default.
 * onChange: (next: string) => void
 */
export const DateTimeInput = React.forwardRef(function DateTimeInput(
  {
    value, onChange, disabled,
    placeholder = "Válassz dátumot és időt",
    className, fromYear = 1940, toYear = 2040,
    clearable = true,
    ...rest
  },
  ref
) {
  const [open, setOpen] = React.useState(false);
  const testId = rest["data-testid"];

  // Parse the current value into date + hour + minute
  const parsed = React.useMemo(() => {
    if (!value || typeof value !== "string") return null;
    const d = parse(value, "yyyy-MM-dd'T'HH:mm", new Date());
    return isValid(d) ? d : null;
  }, [value]);

  // Local draft date used while popover is open (defaults to current time)
  const [draftDate, setDraftDate] = React.useState(parsed);
  const [hour, setHour] = React.useState(parsed ? parsed.getHours() : 12);
  const [minute, setMinute] = React.useState(parsed ? parsed.getMinutes() : 0);

  React.useEffect(() => {
    if (open) {
      setDraftDate(parsed);
      setHour(parsed ? parsed.getHours() : 12);
      setMinute(parsed ? parsed.getMinutes() : 0);
    }
  }, [open, parsed]);

  const commit = (d, h, m) => {
    if (!d) return;
    const next = new Date(d);
    next.setHours(h, m, 0, 0);
    onChange(format(next, "yyyy-MM-dd'T'HH:mm"));
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
  };

  const setPreset = (preset) => {
    const [h, m] = preset.split(":").map((n) => parseInt(n, 10));
    setHour(h); setMinute(m);
    if (draftDate) commit(draftDate, h, m);
  };

  return (
    <Popover open={open} onOpenChange={(v) => !disabled && setOpen(v)}>
      <PopoverTrigger asChild>
        <button
          ref={ref}
          type="button"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "group flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors hover:border-foreground/30 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            !parsed && "text-muted-foreground",
            className
          )}
        >
          <span className="truncate text-left tabular-nums">
            {parsed
              ? format(parsed, "yyyy. MM. dd. HH:mm")
              : placeholder}
          </span>
          <span className="ml-2 flex shrink-0 items-center gap-1 text-muted-foreground">
            {clearable && parsed && !disabled && (
              <span
                role="button"
                aria-label="Időpont törlése"
                onClick={handleClear}
                className="rounded p-0.5 hover:bg-foreground/[0.06] hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <CalendarClock className="h-3.5 w-3.5" strokeWidth={1.75} />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-auto rounded-xl border border-border/80 bg-popover p-0 shadow-lg"
      >
        <div className="flex flex-col gap-0 sm:flex-row">
          <div className="border-b border-border/60 sm:border-b-0 sm:border-r">
            <StyledCalendar
              selected={draftDate || undefined}
              onSelect={(d) => setDraftDate(d || null)}
              locale={hu}
              fromYear={fromYear}
              toYear={toYear}
            />
          </div>

          <div className="flex w-[230px] flex-col p-3">
            <div className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.12em] text-muted-foreground/80">
              Időpont
            </div>
            <div className="flex items-center justify-center gap-2">
              <TimeStepper value={hour} onChange={setHour} min={0} max={23} testId="dt-hour" />
              <span className="text-2xl font-semibold text-muted-foreground/60">:</span>
              <TimeStepper value={minute} onChange={setMinute} min={0} max={59} step={5} testId="dt-minute" />
            </div>
            <div className="mt-3">
              <div className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.12em] text-muted-foreground/80">
                Gyors választás
              </div>
              <div className="grid grid-cols-3 gap-1">
                {TIME_PRESETS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPreset(p)}
                    className="rounded-md border border-border/60 px-2 py-1 text-[11.5px] font-medium tabular-nums transition-colors hover:bg-foreground/[0.06]"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-auto pt-3">
              <button
                type="button"
                disabled={!draftDate}
                onClick={() => commit(draftDate, hour, minute)}
                className="w-full rounded-md bg-foreground px-3 py-2 text-[12.5px] font-medium text-background transition-colors hover:bg-foreground/90 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="dt-commit"
              >
                Beállítás
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border/60 px-3 py-2">
          <button
            type="button"
            onClick={() => {
              const now = new Date();
              setDraftDate(now);
              setHour(now.getHours());
              setMinute(now.getMinutes() - (now.getMinutes() % 5));
            }}
            className="rounded-md px-2 py-1 text-[11.5px] font-medium text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
          >
            Most
          </button>
          {clearable && parsed && (
            <button
              type="button"
              onClick={() => { onChange(""); setOpen(false); }}
              className="rounded-md px-2 py-1 text-[11.5px] font-medium text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
            >
              Törlés
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
});

// re-export DateInput so consumers can pick whichever they need from one module
export { DateInput };
